/** Profile form fields used by both "Finish Your Profile" and the Profile page. */
import { INTERESTS, MONTHS, formatPhone } from "../../shared/constants";
import type { SelfView } from "../../shared/types";
import { $, esc, toast } from "../ui";
import { on } from "../state";

export function nameFields(u: SelfView) {
  return `<div class="grid" style="grid-template-columns:1fr 1fr"><div><label class="fld" for="pf-first">First Name</label><input type="text" id="pf-first" value="${esc(u.first)}" autocomplete="given-name" maxlength="60"></div><div><label class="fld" for="pf-last">Last Name</label><input type="text" id="pf-last" value="${esc(u.last)}" autocomplete="family-name" maxlength="60"></div></div>`;
}

export function phoneField(u: SelfView) {
  if (!u.roles.pa) return "";
  return `<div><label class="fld" for="pf-phone">Mobile Phone <span class="muted" style="font-weight:400">(Optional)</span></label><input type="tel" id="pf-phone" value="${esc(u.phone || "")}" autocomplete="tel-national" data-change="fmt-phone" placeholder="(305) 555-0142"></div>`;
}

export function classOfField(u: SelfView, editable: boolean) {
  if (!u.roles.pa && !u.roles.sa) return "";
  const help = u.roles.pa ? "Year your youngest child that currently attends MCDS graduates" : "Your graduation year";
  return `<div><label class="fld" for="pf-class">Class of</label><input type="number" id="pf-class" inputmode="numeric" min="2000" max="2100" value="${u.classOf ?? ""}" ${editable ? "" : "disabled"} placeholder="e.g. 2031">
  <p class="muted small" style="margin:4px 0 0">${help}</p></div>`;
}

export function birthdayField(u: SelfView) {
  if (!u.roles.pa && !u.roles.sa) return "";
  return `<div><label class="fld" for="pf-bm">Your Birthday <span class="muted" style="font-weight:400">(Optional)</span></label><div class="grid" style="grid-template-columns:2fr 1fr"><select class="f" id="pf-bm" aria-label="Birth month"><option value="">Month</option>${MONTHS.map(
    (m, i) => `<option value="${i + 1}" ${u.birthMonth === i + 1 ? "selected" : ""}>${m}</option>`,
  ).join("")}</select><select class="f" id="pf-bd" aria-label="Birth day"><option value="">Day</option>${Array.from(
    { length: 31 },
    (_, i) => `<option ${u.birthDay === i + 1 ? "selected" : ""}>${i + 1}</option>`,
  ).join("")}</select></div></div>`;
}

export function interestsField(u: SelfView) {
  if (!u.roles.pa && !u.roles.sa) return "";
  const list = INTERESTS.filter((i) => u.roles.pa || i.sa);
  return `<div><label class="fld">Event Interests</label><div class="row wrap" style="gap:8px 18px">${list
    .map(
      (i) =>
        `<label class="row" style="gap:8px;cursor:pointer"><input type="checkbox" class="check pf-int" value="${i.key}" ${u.interests.includes(i.key) ? "checked" : ""}> ${esc(i.label)}</label>`,
    )
    .join("")}</div><p class="muted small" style="margin:4px 0 0">You'll hear about last-minute openings for these.</p></div>
  <label class="row" style="gap:10px;cursor:pointer;align-items:flex-start"><input type="checkbox" class="check" id="pf-shadow" ${u.shadowOnly ? "checked" : ""}><span>Currently Only Interested in Shadowing on Tours</span></label>`;
}

/** Reads whichever of the fields above are on screen. */
export function collectProfile(): Record<string, unknown> {
  const b: Record<string, unknown> = {};
  const v = (id: string) => ($("#" + id) as HTMLInputElement | null)?.value;
  if ($("#pf-first")) b.first = v("pf-first")!.trim();
  if ($("#pf-last")) b.last = v("pf-last")!.trim();
  if ($("#pf-phone")) b.phone = v("pf-phone")!.trim();
  const cls = $("#pf-class") as HTMLInputElement | null;
  if (cls && !cls.disabled) b.classOf = cls.value ? Number(cls.value) : null;
  if ($("#pf-bm")) {
    b.birthMonth = v("pf-bm") || null;
    b.birthDay = v("pf-bd") || null;
  }
  if (document.querySelector(".pf-int")) b.interests = [...document.querySelectorAll<HTMLInputElement>(".pf-int:checked")].map((i) => i.value);
  if ($("#pf-shadow")) b.shadowOnly = ($("#pf-shadow") as HTMLInputElement).checked;
  return b;
}

on({
  "fmt-phone": (el) => {
    const inp = el as HTMLInputElement;
    if (!inp.value.trim()) return;
    const f = formatPhone(inp.value);
    if (f) inp.value = f;
    else toast("Enter a 10-digit phone number");
  },
});
